import React from 'react';
import { Box, Tabs, Tab, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { glass, ADMIN_GRADIENTS } from './glassTokens';

/**
 * GlassTabs — MUI Tabs wrapper with framer-motion layoutId pill indicator.
 * Sticky at top of scroll containers.
 *
 * Props:
 *   value        — current tab value
 *   onChange     — (event, newValue) => void
 *   tabs         — array of { value, label, icon? }
 *   layoutId     — unique string for framer layoutId (default: 'admin-tabs')
 */
export default function GlassTabs({
  value,
  onChange,
  tabs = [],
  layoutId = 'admin-tabs',
  sx = {},
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const reducedMotion = useReducedMotion();
  const g = glass(theme);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 10,
        ...g.surface,
        borderRadius: 2,
        px: 1,
        py: 0.5,
        mb: 3,
        ...sx,
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          minHeight: 44,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
            position: 'relative',
            zIndex: 1,
            color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)',
            transition: 'color 0.18s',
            '&.Mui-selected': {
              color: isDark ? '#fff' : '#0F172A',
            },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, position: 'relative' }}>
                {tab.icon}
                <span>{tab.label}</span>
                {/* Framer pill indicator */}
                {value === tab.value && !reducedMotion && (
                  <Box
                    component={motion.span}
                    layoutId={layoutId}
                    sx={{
                      position: 'absolute',
                      inset: '-6px -12px',
                      borderRadius: '8px',
                      background: isDark
                        ? 'rgba(99,102,241,0.18)'
                        : 'rgba(99,102,241,0.10)',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.35)' : 'rgba(99,102,241,0.25)'}`,
                      zIndex: -1,
                    }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                  />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
}
